import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget. Renders the challenge and reports the resulting
 * token via onVerify. When VITE_TURNSTILE_SITE_KEY is unset (local dev) it renders
 * nothing and immediately reports an empty token - the gateway's verifier is a
 * no-op in that case, so the gated tool still works without a widget.
 */

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

/** Loads the Turnstile script once, resolving when window.turnstile is ready. */
const loadScript = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

interface TurnstileProps {
  onVerify: (token: string) => void;
  /** Called when the token expires or errors, so the caller can disable submit. */
  onReset?: () => void;
}

export const Turnstile = ({ onVerify, onReset }: TurnstileProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  // Latest callbacks in refs so the mount-once effect never needs them as deps.
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  useEffect(() => {
    // Unconfigured (dev): auto-pass with an empty token.
    if (!SITE_KEY) {
      onVerifyRef.current('');
      return;
    }
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onVerifyRef.current(token),
          'expired-callback': () => onResetRef.current?.(),
          'error-callback': () => onResetRef.current?.(),
        });
      })
      .catch(() => onResetRef.current?.());
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* widget already gone */
        }
      }
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={ref} className="flex justify-center" />;
};

/** Whether a Turnstile site key is configured (i.e. the widget will render). */
export const turnstileEnabled = Boolean(SITE_KEY);
